/**
 * DEVICE-CLASS FLAGS ON `<html>`
 * ------------------------------
 * Spotlite's copy of the vocabulary the operator app stamps
 * (`eagleEye/src/app/helpers/device-class-flags.helper.ts`). The two apps build
 * and ship separately, so the names are shared by convention rather than by
 * import: a class means the same thing in both, and neither can import the
 * other's code.
 *
 * | Class             | Meaning                                              |
 * | ----------------- | ---------------------------------------------------- |
 * | `ee-native-build` | Any installed package — the iOS kiosk build           |
 * | `ee-web`          | A browser, including one open on a kiosk URL          |
 * | `ee-ios`          | `Capacitor.getPlatform() === 'ios'`                   |
 * | `ee-android`      | `Capacitor.getPlatform() === 'android'`               |
 *
 * Spotlite has no device roles, so the operator app's `ee-pos` / `ee-kds` /
 * `ee-gfd` / `ee-handheld` / `ee-stationary` / `ee-native` flags have no
 * counterpart here. Add to this set rather than inventing a parallel one, and
 * keep any name that exists in both apps meaning the same thing in both.
 *
 * `ee-native-build` answers one question: is this an installed package with no
 * browser chrome, no back button, and no way home once the webview follows a
 * link out of the app? It is deliberately independent of the kiosk ROUTE and of
 * the stored venue configuration. `KioskContextService.isKiosk` is the routing
 * answer and latches only once a `/kiosk` route is entered, which is after the
 * first paint; the stored configuration dies in a container wipe (EAG-2226).
 * The platform id survives both.
 *
 * PRESENTATION ONLY, same caveat the operator app's copy carries: a CSS
 * affordance, not a security control. Anything that must be genuinely
 * unreachable on a kiosk needs a real guard.
 */
export const DEVICE_CLASS_FLAGS = [
  'ee-native-build',
  'ee-web',
  'ee-ios',
  'ee-android'
] as const;

export type DeviceClassFlag = typeof DEVICE_CLASS_FLAGS[number];

export interface DeviceClassInputs {
  /** `Capacitor.isNativePlatform()` */
  isNative: boolean;
  /** `Capacitor.getPlatform()` — 'ios' | 'android' | 'web' | 'unknown' */
  platform: string;
}

/**
 * Pure computation of the device-class set for the given signals. Kept separate
 * from the DOM write so it is trivially unit-testable.
 */
export function computeDeviceClassFlags(inputs: DeviceClassInputs): DeviceClassFlag[] {
  const { isNative, platform } = inputs;
  const flags: DeviceClassFlag[] = [isNative ? 'ee-native-build' : 'ee-web'];

  if (isNative) {
    if (platform === 'ios') {
      flags.push('ee-ios');
    } else if (platform === 'android') {
      flags.push('ee-android');
    }
  }

  return flags;
}

/**
 * Reconcile the device-class flags on `<html>` against `inputs`. Safe to call
 * repeatedly — it only adds/removes classes it owns (`DEVICE_CLASS_FLAGS`), so
 * classes stamped by anything else survive.
 */
export function applyDeviceClassFlags(
  inputs: DeviceClassInputs,
  element: HTMLElement = document.documentElement
): DeviceClassFlag[] {
  const next = computeDeviceClassFlags(inputs);
  const nextSet = new Set<string>(next);

  for (const flag of DEVICE_CLASS_FLAGS) {
    element.classList.toggle(flag, nextSet.has(flag));
  }

  return next;
}
